import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  CalendarDays,
  Headset,
  KanbanSquare,
  ListChecks,
  Plug,
  Radar,
  ShieldCheck,
  Shuffle,
  Tag,
  Users,
  UsersRound,
} from "lucide-react";
import type { ComponentType } from "react";
import { PageBody, PageHeader } from "@/components/crm/AppShell";
import { AttendanceTab } from "@/components/settings/AttendanceTab";
import { CustomFieldsTab } from "@/components/settings/CustomFieldsTab";
import { IntegrationsTab } from "@/components/settings/IntegrationsTab";
import { PipelinesTab } from "@/components/settings/PipelinesTab";
import { PrivacyTab } from "@/components/settings/PrivacyTab";
import { RoutingTab } from "@/components/settings/RoutingTab";
import { SchedulingTab } from "@/components/settings/SchedulingTab";
import { TagsTab } from "@/components/settings/TagsTab";
import { TeamsTab } from "@/components/settings/TeamsTab";
import { TrackingTab } from "@/components/settings/TrackingTab";
import { UsersTab } from "@/components/settings/UsersTab";
import { cn } from "@/lib/utils";

/**
 * As seções de configuração, na ordem em que aparecem.
 *
 * A descrição de uma linha existe para a navegação lateral: com onze abas,
 * "Distribuição" e "Atendimento" sozinhas não dizem o que muda em cada uma.
 * Cada aba só monta o próprio conteúdo quando está ativa — todas buscam dados
 * próprios, e manter as inativas montadas dispararia requisições que ninguém
 * vai ver.
 */
const ABAS = [
  { value: "usuarios", label: "Usuários", description: "Quem entra e com qual papel", icon: Users },
  {
    value: "equipes",
    label: "Equipes",
    description: "Grupos por especialidade",
    icon: UsersRound,
  },
  { value: "etiquetas", label: "Etiquetas", description: "Marcações livres do lead", icon: Tag },
  {
    value: "funis",
    label: "Funis",
    description: "Etapas do quadro e motivos de perda",
    icon: KanbanSquare,
  },
  { value: "campos", label: "Campos", description: "Dados extras na ficha", icon: ListChecks },
  {
    value: "agenda",
    label: "Agenda",
    description: "Profissionais, salas e expediente",
    icon: CalendarDays,
  },
  {
    value: "atendimento",
    label: "Atendimento",
    description: "Fila, horário e respostas rápidas",
    icon: Headset,
  },
  {
    value: "distribuicao",
    label: "Distribuição",
    description: "Regras de quem recebe o lead",
    icon: Shuffle,
  },
  {
    value: "integracoes",
    label: "Integrações",
    description: "WhatsApp, Meta, IA e API",
    icon: Plug,
  },
  { value: "tracking", label: "Tracking", description: "Tag do site e identificação", icon: Radar },
  {
    value: "privacidade",
    label: "Privacidade",
    description: "LGPD: exportar e anonimizar",
    icon: ShieldCheck,
  },
] as const satisfies ReadonlyArray<{
  value: string;
  label: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
}>;

type Aba = (typeof ABAS)[number]["value"];

const ehAba = (v: string): v is Aba => ABAS.some((a) => a.value === v);

export const Route = createFileRoute("/_authenticated/configuracoes")({
  // `?aba=` existe para quem volta de fora: o OAuth da Meta devolve o
  // navegador aqui, e cair na aba de usuários deixaria a pessoa sem ver o
  // passo que falta concluir. Como a aba vive na URL, o endereço da barra
  // também continua valendo quando alguém o copia para um colega.
  validateSearch: (search: Record<string, unknown>): { aba?: Aba } =>
    typeof search["aba"] === "string" && ehAba(search["aba"]) ? { aba: search["aba"] } : {},
  component: ConfiguracoesPage,
});

/**
 * Configurações.
 *
 * Navegação de aplicativo: em tela larga uma coluna fixa à esquerda, com
 * ícone, nome e uma linha de contexto por seção; no celular, um trilho
 * horizontal de chips que rola com o dedo. O conteúdo tem largura máxima
 * porque formulário de configuração esticado a 1600px deixa o rótulo num
 * canto e o campo no outro.
 */
function ConfiguracoesPage() {
  const { aba } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const ativa: Aba = aba ?? "usuarios";

  const trocar = (proxima: Aba) => {
    if (proxima === ativa) return;
    void navigate({ search: { aba: proxima }, replace: true });
  };

  return (
    <>
      <PageHeader
        title="Configurações"
        description="Quem tem acesso e como o trabalho é distribuído"
      />
      <PageBody className="pb-10">
        <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
          <nav
            aria-label="Seções de configuração"
            className="lg:sticky lg:top-0 lg:w-56 lg:shrink-0 lg:self-start lg:pt-1"
          >
            <div
              role="tablist"
              aria-orientation="horizontal"
              className={cn(
                // Trilho de chips que rola por baixo do gutter da página.
                "scrollbar-none -mx-4 flex gap-1.5 overflow-x-auto px-4 pb-1 sm:-mx-6 sm:px-6",
                "lg:mx-0 lg:flex-col lg:gap-0.5 lg:overflow-visible lg:px-0 lg:pb-0",
              )}
            >
              {ABAS.map((item) => {
                const Icon = item.icon;
                const selecionada = item.value === ativa;
                return (
                  <button
                    key={item.value}
                    type="button"
                    role="tab"
                    id={`aba-${item.value}`}
                    aria-selected={selecionada}
                    aria-controls={`painel-${item.value}`}
                    tabIndex={selecionada ? 0 : -1}
                    onClick={() => trocar(item.value)}
                    className={cn(
                      "group flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                      "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40",
                      "lg:w-full lg:items-start lg:gap-3 lg:whitespace-normal lg:rounded-lg lg:border-transparent lg:px-3 lg:py-2 lg:text-left lg:text-sm",
                      selecionada
                        ? "border-primary/40 bg-primary/10 text-primary lg:bg-muted lg:text-foreground"
                        : "border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground lg:bg-transparent",
                    )}
                  >
                    <Icon
                      className={cn(
                        "size-3.5 shrink-0 lg:mt-0.5 lg:size-4",
                        selecionada
                          ? "text-primary"
                          : "text-muted-foreground group-hover:text-foreground",
                      )}
                    />
                    <span className="min-w-0">
                      <span className="block leading-tight">{item.label}</span>
                      <span className="mt-0.5 hidden text-xs font-normal leading-snug text-muted-foreground lg:block">
                        {item.description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </nav>

          {/* `key` remonta o painel a cada troca: é o que dá a animação de
              entrada e garante que o estado de formulário de uma aba não vaze
              para a seguinte. */}
          <div
            key={ativa}
            role="tabpanel"
            id={`painel-${ativa}`}
            aria-labelledby={`aba-${ativa}`}
            className="min-w-0 max-w-4xl flex-1 animate-rise"
          >
            <Conteudo aba={ativa} />
          </div>
        </div>
      </PageBody>
    </>
  );
}

function Conteudo({ aba }: { aba: Aba }) {
  switch (aba) {
    case "usuarios":
      return <UsersTab />;
    case "equipes":
      return <TeamsTab />;
    case "etiquetas":
      return <TagsTab />;
    case "funis":
      return <PipelinesTab />;
    case "campos":
      return <CustomFieldsTab />;
    case "agenda":
      return <SchedulingTab />;
    case "atendimento":
      return <AttendanceTab />;
    case "distribuicao":
      return <RoutingTab />;
    case "integracoes":
      return <IntegrationsTab />;
    case "tracking":
      return <TrackingTab />;
    case "privacidade":
      return <PrivacyTab />;
  }
}
